// ═══════════════════════════════════════════════════════════════════════════════
// Fixed primary action above bottom nav (mobile shell)
// Align horizontal inset with MainAppShell column (max-w-lg = 32rem) on wide viewports.
// ═══════════════════════════════════════════════════════════════════════════════

import { Link } from 'react-router-dom';
import {
  PAGE_PRIMARY_FAB_ABOVE_REVIEW_ROW_BOTTOM,
} from '../../lib/layoutChrome';

const PAGE_PRIMARY_FAB_BASE_CLASS =
  'fixed z-50 inline-flex max-w-[min(100vw-2rem,20rem)] items-center gap-2 rounded-full bg-primary-600 py-3 pl-4 pr-5 text-sm font-semibold text-white shadow-lg shadow-primary-600/25 transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:shadow-primary-900/40 right-[max(1rem,calc((100vw-32rem)/2+1rem))]';

const PAGE_PRIMARY_FAB_BOTTOM_DEFAULT =
  'bottom-[calc(4rem+max(16px,env(safe-area-inset-bottom,0px))+12px)]';

type PagePrimaryFabPosition = 'default' | 'aboveReviewBar';

type PagePrimaryFabCommonProps = {
  ariaLabel: string;
  children: React.ReactNode;
  position?: PagePrimaryFabPosition;
};

type PagePrimaryFabLinkProps = PagePrimaryFabCommonProps & {
  to: string;
};

type PagePrimaryFabButtonProps = PagePrimaryFabCommonProps & {
  onClick: () => void;
};

const resolveFabClassName = (position: PagePrimaryFabPosition): string => {
  const bottomClass =
    position === 'aboveReviewBar'
      ? PAGE_PRIMARY_FAB_ABOVE_REVIEW_ROW_BOTTOM
      : PAGE_PRIMARY_FAB_BOTTOM_DEFAULT;
  return `${PAGE_PRIMARY_FAB_BASE_CLASS} ${bottomClass}`;
};

export const PagePrimaryFab = (props: PagePrimaryFabLinkProps | PagePrimaryFabButtonProps) => {
  const position = props.position ?? 'default';
  const className = resolveFabClassName(position);
  if ('to' in props) {
    return (
      <Link to={props.to} className={className} aria-label={props.ariaLabel}>
        {props.children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={props.onClick} className={className} aria-label={props.ariaLabel}>
      {props.children}
    </button>
  );
};
